import React from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import clsx from 'clsx';
import styles from './PriceLists.module.scss';
import InfoIcon from 'assets/icons/info.svg?react';
import { Tip } from '../../../components/uikit/Tip.tsx';
import formStyles from '../../../components/form/form.module.scss';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import { onContextMenuPreparing } from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { PriceListsQuery } from 'services/api/api-client.ts';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { useProcessLinks } from '../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { Trans, UseTranslationResponse } from 'react-i18next';
import { IColumnProps } from 'devextreme-react/cjs/data-grid';
import {
  DownloadButton,
  DownloadButtonProps,
} from './components/DownloadButton.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { TranslationKeys } from '@excalidraw/excalidraw/types/i18n';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

const mail = 'supportHC@loewensteinmedical.com';

export const PriceLists = () => {
  const i18n = useScopedTranslation('Page.PriceLists');

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    isTooltipVisible,
    showTooltip,
    closeTooltip,
  } = useProcessLinks();

  const priceListQuery = PriceListsQuery.useGetQuery(
    {},
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  useDataGrid();

  const downloadButtonProps: Omit<DownloadButtonProps, 'cell' | 'isDownload'> =
    {
      removeProcessingLink,
      processingLinks,
      showTooltip,
      addProcessingLink,
    };

  const columnsConfig = getColumnsConfig(i18n, downloadButtonProps);

  const errObj = priceListQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const keys = Object.keys(priceListQuery.data?.records ?? {});
  const isPlaceholderVisible =
    !keys.length && !priceListQuery.isLoading && !isExternalServiceUnavailable;

  return (
    <div data-test-id={'price-lists-container'}>
      <Breadcrumbs />
      <Loading
        loading={priceListQuery.isLoading || priceListQuery.isPlaceholderData}
      >
        {priceListQuery.data?.lastValid && (
          <div className={clsx(styles.infoBlock)}>
            <InfoIcon />
            <div className={styles.bold}>
              {i18n.t('updated')}
              {` ${formatDate(priceListQuery.data.lastValid)}`}
            </div>
            <Tip
              tooltipText={i18n.t('tooltip_text')}
              defaultIconClassname={styles.tooltipIcon}
              className={styles.tooltip}
              tooltipClassName={styles.tooltipText}
            />
          </div>
        )}
        {keys.map((key, index) => {
          const data = priceListQuery.data?.records[key];

          if (!data) {
            return null;
          }

          return (
            <div
              className={clsx(
                index !== keys.length - 1 && styles.marginBottom40,
              )}
              key={key}
            >
              <div className={styles.tableHeader}>
                {i18n.t(key.toLowerCase() as TranslationKeys, {
                  defaultValue: key,
                })}
              </div>
              <DXG.DataGrid
                dataSource={data}
                className={clsx(
                  gridStyles.editorOutline,
                  formStyles.table,
                  styles.fix,
                )}
                sorting={{ mode: 'none' }}
                onContextMenuPreparing={onContextMenuPreparing}
              >
                {columnsConfig.map((x, index) => {
                  return (
                    <DXG.Column
                      cssClass={clsx(x.cssClass, formStyles.text)}
                      key={`${x.dataField}.${index}`}
                      showEditorAlways={x.allowEditing}
                      {...x}
                    />
                  );
                })}
              </DXG.DataGrid>
            </div>
          );
        })}
        {isTooltipVisible && (
          <Tooltip
            isError={true}
            closeTooltip={closeTooltip}
            bodyContent={
              <Trans
                i18nKey={'fileNotFound'}
                components={{
                  supportLink: <a href={`mailto:${mail}`} />,
                }}
              />
            }
            className={styles.errorTooltip}
            showCancelButton={true}
            iconClassName={styles.errorTooltipIcon}
          />
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && (
          <TablePlaceholder containerClassname={styles.marginTop152} />
        )}
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.PriceLists'>,
  downloadButtonProps: Omit<DownloadButtonProps, 'cell' | 'isDownload'>,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'name',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('name'),
    cssClass: clsx(formStyles.textCellPadding, styles.whiteSpaceNormal),
    minWidth: 400,
  },
  {
    dataField: 'linkCsv',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 136,
    cellRender: (cell) => (
      <DownloadButton cell={cell} {...downloadButtonProps} />
    ),
  },
  {
    dataField: 'linkPdf',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 136,
    cellRender: (cell) => (
      <DownloadButton cell={cell} {...downloadButtonProps} />
    ),
  },
];
